const { get } = require('lodash');
const { stats, logger } = require('@fiverr-private/obs');
const { METRICS_PREFIX } = require('../constants');
const { isValidServiceName } = require('../../../configuration/serviceNames');

/**
 * @typedef {import('../constants')['PERSEUS_NAME']} PERSEUS_NAME
 * @typedef {PERSEUS_NAME | keyof (typeof import('../../../configuration')['config'])['services']} ServiceName
 */

/**
 * @param {() => Promise<any>} promise
 * @param {ServiceName} serviceName - service name
 * @param {object} options - params for custom use
 * @param {string} options.requestName - request name
 * @param {boolean=} options.pathfinderUIRequest - request from UI via pathfinder (public API)
 * @returns {Promise<any>}
 */
const trackRequestMetrics = async (promise, serviceName, options) => {
    const { requestName, pathfinderUIRequest } = options;
    if (!pathfinderUIRequest && !isValidServiceName(serviceName)) {
        logger.error(`"serviceName" metric not defined: "${serviceName}"`, { details: { serviceName, requestName } });
        return promise;
    }

    const fetchStartTime = new Date();

    try {
        const data = await promise();
        stats.count(METRICS_PREFIX, `${serviceName}.${requestName}.success`);
        stats.time(METRICS_PREFIX, `${serviceName}.${requestName}.success_time`, fetchStartTime);

        return data;
    } catch (error) {
        const status = get(error, 'response.status', 'unknown');
        stats.count(METRICS_PREFIX, `${serviceName}.${requestName}.error`);
        stats.time(METRICS_PREFIX, `${serviceName}.${requestName}.error_time.${status}`, fetchStartTime);
        throw error;
    }
};

/**
 * @param {(...args: any[]) => any} requestFunc - request name
 * @param {ServiceName} serviceName - request name
 * @param {object} options - params for custom use
 * @param {string=} options.requestName - request name
 * @param {boolean=} options.pathfinderUIRequest - request from UI via pathfinder (public API)
 * @returns {(...args: any[]) => Promise<any>}
 */
const wrapTrackRequestMetrics =
    (requestFunc, serviceName, { requestName, pathfinderUIRequest = false } = {}) =>
    (...args) =>
        trackRequestMetrics(() => requestFunc(...args), serviceName, {
            requestName: requestName || requestFunc.name,
            pathfinderUIRequest,
        });

module.exports = {
    trackRequestMetrics,
    wrapTrackRequestMetrics,
};
